/*
 * Permission Utility
 * 
 * Features:
 * - Determines edit permissions for accounts and contacts based on team membership
 * - Supports universal view access with team-based edit restrictions
 * - Handles both team accounts and RLP accounts
 * 
 * Implementation:
 * - Users can always view all accounts and contacts (universal access)
 * - Users can only edit accounts/contacts that belong to their teams
 * - RLP accounts (rlpCompanyId !== null) are editable by all users
 * - Special handling for user creation permissions (createdBy check)
 * 
 * Changes:
 * - 2026-01-26: Created for universal view access feature with team-based edit restrictions
 */

export interface PermissionCheckData {
  teamId?: number | null;
  rlpCompanyId?: number | null;
  createdBy?: number | null;
}

export interface UserTeam {
  userId: number;
  teamId: number;
  teamName: string;
}

/**
 * Checks if the current user can edit the given account or contact
 * @param data - The account or contact data containing teamId, rlpCompanyId, and createdBy
 * @param userTeams - Array of teams the current user belongs to
 * @param currentUserId - The current user's ID
 * @returns true if the user can edit, false if view-only
 */
export const canEdit = (
  data: PermissionCheckData,
  userTeams: UserTeam[],
  currentUserId: number | null
): boolean => {
  // If no current user, no edit permissions
  if (!currentUserId) {
    return false;
  }

  // RLP entities pass edit permission check (for UI consistency with RLP chip)
  // Note: RLP schema sections are always read-only (enforced in useFilteredSchemas)
  if (!!data.rlpCompanyId) {
    return true;
  }

  // If the current user created this record, they can edit it
  if (data.createdBy === currentUserId) {
    return true;
  }

  // If no teamId, it's a system record - only creator can edit
  if (!data.teamId) {
    return false;
  }

  // Check if user is member of the record's team
  const userTeamIds = userTeams.map(team => team.teamId);
  return userTeamIds.includes(data.teamId);
};

/**
 * Checks if the current user can delete the given account or contact
 * Currently only allows deletion by the creator
 * @param data - The account or contact data
 * @param currentUserId - The current user's ID
 * @returns true if the user can delete, false otherwise
 */
export const canDelete = (
  data: PermissionCheckData,
  currentUserId: number | null
): boolean => {
  if (!currentUserId || !data.createdBy) {
    return false;
  }

  return data.createdBy === currentUserId;
};

/**
 * Gets the permission level for the current user on the given record
 * @param data - The account or contact data
 * @param userTeams - Array of teams the current user belongs to
 * @param currentUserId - The current user's ID
 * @returns 'edit' | 'view-only'
 */
export const getPermissionLevel = (
  data: PermissionCheckData,
  userTeams: UserTeam[],
  currentUserId: number | null
): 'edit' | 'view-only' => {
  return canEdit(data, userTeams, currentUserId) ? 'edit' : 'view-only';
};

/**
 * Gets a user-friendly permission description
 * @param data - The account or contact data
 * @param userTeams - Array of teams the current user belongs to  
 * @param currentUserId - The current user's ID
 * @returns A string describing the user's permissions
 */
export const getPermissionDescription = (
  data: PermissionCheckData,
  userTeams: UserTeam[],
  currentUserId: number | null
): string => {
  if (canEdit(data, userTeams, currentUserId)) {
    if (!!data.rlpCompanyId) {
      return "RLP record - full access";
    } else if (data.createdBy === currentUserId) {
      return "Created by you - full access";
    } else {
      return "Team member - full access";
    }
  } else {
    return "Limited access - basic fields only";
  }
};